import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useFormik } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DateTimePicker from "../Inputs/DateTimePicker";
import {
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const makeInitial = (filter = {}) => ({
  status_filter: filter.status_filter || "",
  target_class: filter.target_class || "",
  camera_id: Array.isArray(filter.camera_id)
    ? filter.camera_id
    : filter.camera_id
    ? filter.camera_id.split(",").filter(Boolean)
    : [],
  startDate: filter.startDate
    ? dayjs(filter.startDate, ["YYYY-MM-DD", "DD/MM/YYYY"])
    : filter.start_date
    ? dayjs(filter.start_date, "YYYY-MM-DD")
    : null,
  endDate: filter.endDate
    ? dayjs(filter.endDate, ["YYYY-MM-DD", "DD/MM/YYYY"])
    : filter.end_date
    ? dayjs(filter.end_date, "YYYY-MM-DD")
    : null,
  startTime: filter.startTime
    ? dayjs(filter.startTime, "HH:mm")
    : filter.start_time
    ? dayjs(filter.start_time, "HH:mm")
    : null,
  endTime: filter.endTime
    ? dayjs(filter.endTime, "HH:mm")
    : filter.end_time
    ? dayjs(filter.end_time, "HH:mm")
    : null,
  buildings: Array.isArray(filter.buildings)
    ? filter.buildings
    : filter.building
    ? [filter.building]
    : filter.buildings?.split(",").filter(Boolean) || [],
  floors: Array.isArray(filter.floors)
    ? filter.floors
    : filter.floor_level
    ? [filter.floor_level]
    : filter.floors?.split(",").filter(Boolean) || [],
  zones: Array.isArray(filter.zones)
    ? filter.zones
    : filter.zone
    ? [filter.zone]
    : filter.zones?.split(",").filter(Boolean) || [],
  areas: Array.isArray(filter.areas)
    ? filter.areas
    : filter.area
    ? [filter.area]
    : filter.areas?.split(",").filter(Boolean) || [],
  locations: Array.isArray(filter.locations)
    ? filter.locations
    : filter.location
    ? [filter.location]
    : filter.locations?.split(",").filter(Boolean) || [],
});

const NoEntryFilter = forwardRef(({ filter, changeFilterHandle }, ref) => {
  const { token } = useRecoilValue(authState);

  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [zones, setZones] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);

  const formik = useFormik({
    initialValues: makeInitial(filter),
    enableReinitialize: true,
    onSubmit: (values) => {
      changeFilterHandle({
        status_filter: values.status_filter || undefined,
        target_class: values.target_class || undefined,
        camera_id: values.camera_id.length > 0 ? values.camera_id.join(",") : undefined,
        start_date: values.startDate ? values.startDate.format("YYYY-MM-DD") : undefined,
        end_date: values.endDate ? values.endDate.format("YYYY-MM-DD") : undefined,
        start_time: values.startTime ? values.startTime.format("HH:mm") : undefined,
        end_time: values.endTime ? values.endTime.format("HH:mm") : undefined,
        building: values.buildings.length > 0 ? values.buildings.join(",") : undefined,
        floor_level: values.floors.length > 0 ? values.floors.join(",") : undefined,
        zone: values.zones.length > 0 ? values.zones.join(",") : undefined,
        area: values.areas.length > 0 ? values.areas.join(",") : undefined,
        location: values.locations.length > 0 ? values.locations.join(",") : undefined,
        // Also preserve legacy naming for other components
        startDate: values.startDate,
        endDate: values.endDate,
        startTime: values.startTime,
        endTime: values.endTime,
        buildings: values.buildings,
        floors: values.floors,
        zones: values.zones,
        areas: values.areas,
        locations: values.locations,
      });
    },
  });

  const clearAllFilters = () => {
    formik.resetForm({
      values: {
        status_filter: "",
        target_class: "",
        locations: [],
        areas: [],
        buildings: [],
        floors: [],
        zones: [],
        camera_id: [],
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
      },
    });

    setAreas([]);
    setBuildings([]);
    setFloors([]);
    setZones([]);
    setCameras([]);
    setHierarchyError(null);
  };

  // 🟢 Fetch Locations
  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      setHierarchyError(null);
      try {
        const { data } = await axios.get(`${baseURL}locations/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLocations(data.locations || []);
      } catch (error) {
        setHierarchyError("Failed to load locations");
        console.error("Error fetching locations:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchLocations();
    }
  }, [token]);

  // 🟢 Fetch Areas when locations change
  useEffect(() => {
    if (formik.values.locations.length > 0) {
      const fetchAreas = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}areas/list?locations=${formik.values.locations.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAreas(data.areas || []);
        } catch (error) {
          setHierarchyError("Failed to load areas");
          console.error("Error fetching areas:", error);
        }
      };
      fetchAreas();
    } else {
      setAreas([]);
      formik.setFieldValue("areas", []);
      setBuildings([]);
      formik.setFieldValue("buildings", []);
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.locations, token]);

  // 🟢 Fetch Buildings when areas change
  useEffect(() => {
    if (formik.values.areas.length > 0) {
      const fetchBuildings = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}buildings/list?areas=${formik.values.areas.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBuildings(data.buildings || []);
        } catch (error) {
          setHierarchyError("Failed to load buildings");
          console.error("Error fetching buildings:", error);
        }
      };
      fetchBuildings();
    } else {
      setBuildings([]);
      formik.setFieldValue("buildings", []);
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.areas, token]);

  // 🟢 Fetch Floors when buildings change
  useEffect(() => {
    if (formik.values.buildings.length > 0) {
      const fetchFloors = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}floor-levels/list?buildings=${formik.values.buildings.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setFloors(data.floor_levels || []);
        } catch (error) {
          setHierarchyError("Failed to load floors");
          console.error("Error fetching floors:", error);
        }
      };
      fetchFloors();
    } else {
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.buildings, token]);

  // 🟢 Fetch Zones when floors change
  useEffect(() => {
    if (formik.values.floors.length > 0) {
      const fetchZones = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}zones/list?floor_levels=${formik.values.floors.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setZones(data.zones || []);
        } catch (error) {
          setHierarchyError("Failed to load zones");
          console.error("Error fetching zones:", error);
        }
      };
      fetchZones();
    } else {
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.floors, token]);

  // 🟢 Fetch Cameras when zones change
  useEffect(() => {
    if (formik.values.zones.length > 0) {
      const fetchCameras = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}streams/locations/search?location_type=zones&q=${formik.values.zones.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCameras(data || []);
        } catch (error) {
          setHierarchyError("Failed to load cameras");
          console.error("Error fetching cameras:", error);
        }
      };
      fetchCameras();
    } else {
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.zones, token]);

  useImperativeHandle(ref, () => ({
    submit: formik.handleSubmit,
  }));

  const handleCheckboxChange = (field, value) => {
    const selected = Array.isArray(formik.values[field]) ? formik.values[field] : [];
    const updated = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    formik.setFieldValue(field, updated);
  };

  const handleSelectAll = (field, options, valueKey) => {
    const allValues = options.map((opt) => opt[valueKey]);
    const currentValues = formik.values[field] || [];
    const allSelected = allValues.every((val) => currentValues.includes(val));

    formik.setFieldValue(field, allSelected ? [] : allValues);
  };

  const renderCheckboxGroup = (
    label,
    field,
    options,
    valueKey = "location",
    isLoading = false,
    displayKey = null
  ) => {
    const selectedCount = formik.values[field]?.length || 0;
    const totalCount = options.length;
    const actualDisplayKey = displayKey || valueKey;

    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={600}>
            {label} {selectedCount > 0 && `(${selectedCount} selected)`}
          </Typography>
          {options.length > 0 && (
            <Button
              size="small"
              onClick={() => handleSelectAll(field, options, valueKey)}
            >
              {selectedCount === totalCount ? "Deselect All" : "Select All"}
            </Button>
          )}
        </Stack>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : options.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={1}>
            No {label.toLowerCase()} available
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 1,
              maxHeight: 180,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1,
            }}
          >
            {options.map((opt) => (
              <FormControlLabel
                key={opt.id || opt[valueKey]}
                control={
                  <Checkbox
                    size="small"
                    checked={formik.values[field]?.includes(opt[valueKey]) || false}
                    onChange={() => handleCheckboxChange(field, opt[valueKey])}
                  />
                }
                label={opt[actualDisplayKey] || opt[valueKey]}
                sx={{ margin: 0 }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight={600}>No-Entry Zone Filters</Typography>
        <Button size="small" onClick={clearAllFilters} color="secondary">
          Clear All Filters
        </Button>
      </Stack>

      <Stack gap={2.5} component="form" onSubmit={formik.handleSubmit}>
        {/* Status & Target Class Selectors */}
        {/* <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Incident Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status_filter"
              name="status_filter"
              value={formik.values.status_filter}
              label="Incident Status"
              onChange={formik.handleChange}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="detected">Detected</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            id="target_class"
            name="target_class"
            label="Target Class (e.g. person, vehicle)"
            value={formik.values.target_class}
            onChange={formik.handleChange}
          />
        </Stack> */}

        {loading && <CircularProgress size={24} />}
        {hierarchyError && <Alert severity="error">{hierarchyError}</Alert>}

        {!loading && !hierarchyError && (
          <>
            {renderCheckboxGroup("Locations", "locations", locations)}
            {formik.values.locations.length > 0 &&
              renderCheckboxGroup("Areas", "areas", areas, "area")}
            {formik.values.areas.length > 0 &&
              renderCheckboxGroup("Buildings", "buildings", buildings, "building")}
            {formik.values.buildings.length > 0 &&
              renderCheckboxGroup("Floors", "floors", floors, "floor_level")}
            {formik.values.floors.length > 0 &&
              renderCheckboxGroup("Zones", "zones", zones, "zone")}
            {formik.values.zones.length > 0 &&
              renderCheckboxGroup("Cameras", "camera_id", cameras, "id", false, "name")}
          </>
        )}

        {/* Date and Time Pickers */}
        <DateTimePicker
          label="Start"
          dateValue={formik.values.startDate}
          timeValue={formik.values.startTime}
          onDateChange={(d) => formik.setFieldValue("startDate", d)}
          onTimeChange={(t) => formik.setFieldValue("startTime", t)}
          maxDate={formik.values.endDate}
        />
        <DateTimePicker
          label="End"
          dateValue={formik.values.endDate}
          timeValue={formik.values.endTime}
          onDateChange={(d) => formik.setFieldValue("endDate", d)}
          onTimeChange={(t) => formik.setFieldValue("endTime", t)}
          minDate={formik.values.startDate}
        />
      </Stack>
    </LocalizationProvider>
  );
});

NoEntryFilter.displayName = "NoEntryFilter";

export default NoEntryFilter;
